import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
} from '@material-ui/core';
import {
  ExpandMore,
  CheckCircle,
  RadioButtonUnchecked,
  PlayCircleFilled,
} from '@material-ui/icons';
import clsx from 'clsx';
import { onboardingSearchActivate } from '../../../../slices/OnboardingSlice';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import styles from './SetupGuide.module.scss';

const SetupGuide = ({ progress, shopId }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();

  const { isServiceUpdating } = useSelector((state) => ({
    isServiceUpdating: state.settings.isUpdatingSearch,
  }));

  const steps = useMemo(() => [
    {
      id: 'panel1',
      title: localizeMessage('cloud.home.setup.connect.title'),
      description: localizeMessage('cloud.home.setup.connect.description'),
      isCompleted: progress.connected,
      actionLabel: progress.connected
        ? localizeMessage('cloud.home.setup.connect.open')
        : localizeMessage('cloud.home.setup.connect.start'),
      action: () => history.push(progress.connected
        ? `/app/${shopId}/search/settings/data-connectivity`
        : `/app/${shopId}/setup`),
      actionVariant: 'outlined',
    },
    {
      id: 'panel2',
      title: localizeMessage('cloud.home.setup.integration.title'),
      description: localizeMessage('cloud.home.setup.integration.description'),
      isCompleted: progress.integration,
      actionLabel: progress.integration
        ? localizeMessage('cloud.home.setup.integration.modify')
        : localizeMessage('cloud.home.setup.integration.start'),
      action: () => history.push(`/app/${shopId}/search/setup`),
      actionVariant: 'contained',
      actionColor: 'primary',
    },
    {
      id: 'panel3',
      title: localizeMessage('cloud.home.setup.activate.title'),
      description: localizeMessage('cloud.home.setup.activate.description'),
      isCompleted: progress.activated,
      actionLabel: !progress.integration
        ? localizeMessage('cloud.home.setup.activate.prerequisite')
        : (isServiceUpdating
          ? localizeMessage('cloud.home.setup.activate.activating')
          : localizeMessage('cloud.home.setup.activate.action')),
      action: () => dispatch(onboardingSearchActivate()),
      actionVariant: 'contained',
      actionColor: 'primary',
      style: !progress.activated ? { backgroundColor: '#4caf50', color: '#fff' } : {},
      isDisabled: isServiceUpdating || !progress.integration,
      hideActionIfCompleted: true,
    },
  ], [progress, shopId, history, dispatch, isServiceUpdating, localizeMessage]);

  // Find first incomplete step to expand by default
  const firstIncompleteStepId = useMemo(() => {
    const firstIncomplete = steps.find((s) => !s.isCompleted);
    return firstIncomplete ? firstIncomplete.id : null;
  }, [steps]);

  const [expanded, setExpanded] = useState(firstIncompleteStepId);

  // Update expanded panel when first incomplete step changes
  useEffect(() => {
    setExpanded(firstIncompleteStepId);
  }, [firstIncompleteStepId]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const completedCount = steps.filter((s) => s.isCompleted).length;
  const progressPercent = (completedCount / steps.length) * 100;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{localizeMessage('cloud.home.setup.title')}</h2>
        <div className={styles.progressText}>
          <span>
            {localizeMessage('cloud.home.setup.progress', {
              completed: completedCount,
              total: steps.length,
            })}
          </span>
          <span>
            {Math.round(progressPercent)}
            %
          </span>
        </div>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className={styles.list}>
        {steps.map((step) => (
          <Accordion
            key={step.id}
            expanded={expanded === step.id}
            onChange={handleChange(step.id)}
            elevation={0}
            className={clsx(styles.accordion, step.isCompleted && styles.completed)}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
            >
              <div className={clsx(styles.stepIcon, step.isCompleted && styles.completed, expanded === step.id && styles.active)}>
                {step.isCompleted ? <CheckCircle /> : (expanded === step.id ? <PlayCircleFilled /> : <RadioButtonUnchecked />)}
              </div>
              <Typography className={clsx(styles.stepTitle, step.isCompleted && styles.completed)}>
                {step.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column', paddingTop: 0 }}>
              <Typography color="textSecondary" variant="body2" paragraph>
                {step.description}
              </Typography>
              <div className={styles.stepActions}>
                {(!step.isCompleted || !step.hideActionIfCompleted) && (
                  <Button
                    variant={step.actionVariant || 'outlined'}
                    color={step.actionColor || 'default'}
                    size="small"
                    onClick={step.action}
                    disabled={step.isDisabled}
                    style={step.style || {}}
                  >
                    {step.actionLabel}
                  </Button>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  );
};

SetupGuide.propTypes = {
  progress: PropTypes.shape({
    connected: PropTypes.bool,
    integration: PropTypes.bool,
    activated: PropTypes.bool,
  }).isRequired,
  shopId: PropTypes.string,
};

export default SetupGuide;
